import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment/moment';
import React, { memo, useCallback, useRef, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormattedMessage, useIntl } from 'react-intl';

import IconSend from '../../components/Icons/IconSend';
import config from '../../config';
import { getClientData, setClientData } from '../../util/clientData';
import useAppSelector from '../../util/hooks/useAppSelector';

const NewsletterFormContent = memo(({ onSubmit }) => {
  const { executeRecaptcha, container } = useGoogleReCaptcha();
  const currentUser = useAppSelector((state) => state.user.currentUser);

  const subscribedCookie = getClientData('vansite.newsletter.subscribed');
  const [subscribed, setSubscribed] = useState(!!subscribedCookie);
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const firstName = currentUser?.attributes?.profile?.firstName || '';
  const firstNameRef = useRef(firstName);
  const email = currentUser?.attributes?.email || '';
  const emailRef = useRef(email);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return;
    }

    if (!containerRef.current) return;

    try {
      return await executeRecaptcha('newsletter');
    } catch (e) {
      // noop
    }
  }, [executeRecaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = await handleReCaptchaVerify();
    if (!token) return;
    onSubmit({
      firstName: firstNameRef.current.value,
      email: emailRef.current.value,
    })
      .then(({ success, code }) => {
        if (success || code === 'already-subscribed') {
          setSubscribed(true);
          setClientData(
            'vansite.newsletter.subscribed',
            true,
            moment().add(1, 'day').toISOString()
          );
        }
      })
      .catch((error) => {
        setError(error);
      });
  };

  const firstNameLabel = <FormattedMessage id="NewsletterForm.name" />;
  const emailLabel = <FormattedMessage id="NewsletterForm.email" />;
  const emailHelperText = <FormattedMessage id="NewsletterForm.email.required" />;

  const subText = (
    <span style={{ color: 'var(--matterColorAnti)', fontSize: '14px' }}>
      <FormattedMessage id="NewsletterForm.checkbox.subText" />
    </span>
  );
  const checkboxLabel = <FormattedMessage id="NewsletterForm.checkbox" values={{ subText }} />;

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={4}>
      <TextField
        defaultValue={firstName}
        fullWidth
        inputRef={firstNameRef}
        label={firstNameLabel}
        name="firstName"
        variant="outlined"
      />
      <TextField
        autoComplete="email"
        defaultValue={email}
        fullWidth
        helperText={emailHelperText}
        inputRef={emailRef}
        label={emailLabel}
        name="email"
        required
        type="email"
        variant="outlined"
      />
      <FormGroup>
        <FormControlLabel
          control={<Checkbox onChange={() => setAccept(!accept)} />}
          label={checkboxLabel}
        />
      </FormGroup>
      <Box id={container} ref={containerRef} />
      {subscribed ? (
        <Typography component="span" variant="h5">
          <FormattedMessage id="NewsletterForm.subscribed" tagName="h5" />
        </Typography>
      ) : (
        <Button
          color="primary"
          disabled={!accept}
          endIcon={<IconSend />}
          type="submit"
          variant="contained"
        >
          <FormattedMessage id="SectionNewsletter.button" />
        </Button>
      )}
      {error ? (
        <Alert severity="error">
          <FormattedMessage id="NewsletterForm.error" />
        </Alert>
      ) : null}
    </Stack>
  );
});

NewsletterFormContent.displayName = 'NewsletterFormContent';

const NewsletterFormComponent = ({ onSubmit, elementId }) => {
  const intl = useIntl();

  return (
    <GoogleReCaptchaProvider
      container={{
        element: elementId,
        parameters: {
          badge: 'inline',
        },
      }}
      language={intl.locale}
      reCaptchaKey={config.google.recaptcha}
    >
      <NewsletterFormContent onSubmit={onSubmit} />
    </GoogleReCaptchaProvider>
  );
};

NewsletterFormComponent.displayName = 'NewsletterForm';

const NewsletterForm = memo(NewsletterFormComponent);

export default NewsletterForm;
